/**
 * One card of the resolve-label-error flow: an icon, a title and a description, with a slot at the
 * end of the row for whatever marks the card - the Recommended chip, the reviewed marker.
 *
 * A card with onClick gets a chevron; one that also takes isExpanded opens its body in place. The
 * body is hidden while the card is closed rather than taken down, because a field registers itself
 * with the screen's validation group as it mounts and withdraws as it unmounts: a closed card would
 * otherwise take its own verdict with it, and a screen full of half-filled forms would read valid.
 */
import TriggerArrow2 from "../../../../../../components/collapsible/trigger-arrow2/trigger-arrow2";
import FontBody2 from "../../../../../../components/fonts/font-body2/font-body2";
import FontBody3 from "../../../../../../components/fonts/font-body3/font-body3";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../../components/fonts/font-props";
import { ICON_SIZE, IconProps } from "../../../../../../components/icons/icon-props-interface";
import Icon from "../../../../../../components/icons/icon/icon";
import Card from "../../../../../../components/layouts/card/card";
import { CARD_SPACING_VARIANT } from "../../../../../../components/layouts/card/card.types";
import "./resolve-label-error-card.scss";

/** The flow icons default to their own accent colour; the design shows them blue. */
const ICON_COLOR = "var(--color-accent-1)";

interface IProps {
    icon: React.FC<IconProps>;
    title: string;
    description: string;
    trailing?: React.ReactNode;
    /** Makes the card clickable and shows the chevron. Without it the card is inert. */
    onClick?: () => void;
    /** Set only on a card that opens in place: rotates the chevron and shows the body. */
    isExpanded?: boolean;
    children?: React.ReactNode;
}

const ResolveLabelErrorCard: React.FC<IProps> = ({
    icon,
    title,
    description,
    trailing,
    onClick,
    isExpanded = false,
    children,
}) => (
    <Card isExpandable={false} spacingVariant={CARD_SPACING_VARIANT.SMAll}>
        <div
            className={`resolve-label-error-card ${onClick ? 'is-clickable' : ''}`}
            onClick={onClick}
        >
            <Icon Component={icon} size={ICON_SIZE.MEDIUM} color={ICON_COLOR} />
            <div className="resolve-label-error-card-text">
                <FontBody2 color={FONT_COLOR.PRIMARY2} variant={[FONT_VARIANT.BOLD]}>
                    {title}
                </FontBody2>
                <FontBody3 color={FONT_COLOR.NEUTRAL3}>
                    {description}
                </FontBody3>
            </div>
            {trailing}
            {onClick && <TriggerArrow2 isExpanded={isExpanded} />}
        </div>
        <div className="resolve-label-error-card-body" hidden={!isExpanded}>{children}</div>
    </Card>
);

export default ResolveLabelErrorCard;
